import { gql } from "@apollo/client";
import restClient from "../../../services/restClient";

export const GET_ME = gql`
  query GetMe {
    me {
      __typename
      userId
      profileImage
      hasCustomProfileImage
      firstName
      lastName
      birthDate
      gender
      email
      phoneNumber
  referralCode
      children {
        __typename
        childId
        firstName
        lastName
        birthDate
        gender
      }
  # permissions removed: use subscription permissions queries/hooks instead
      meta {
        __typename
        allowNewEventEmails
        marketingOptIn
        newEventPushNotification
      }
      isSeller
      isSeller
      canCreateOpenEvents
      reliabilityScore
      reliabilityBadge
      billingAddress {
        __typename
        fullName
        companyName
        vatNumber
        fiscalCode
        addressLine1
        addressLine2
        city
        postalCode
        province
        country
        isCompany
        sdiCode
        pecEmail
      }
    }
  }
`;

export const ADD_CHILD = gql`
  mutation AddChild(
    $firstName: String!
    $lastName: String!
    $birthDate: Time!
    $gender: Gender!
  ) {
    addChild(
      firstName: $firstName
      lastName: $lastName
      birthDate: $birthDate
      gender: $gender
    )
  }
`;

export const DELETE_CHILD = gql`
  mutation RemoveChild($childId: ID!) {
    removeChild(childId: $childId)
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser(
    $profileImage: String
    $firstName: String
    $lastName: String
    $birthDate: Time
    $phoneNumber: String
    $allowNewEventEmails: Boolean
    $marketingOptIn: MarketingOptIn
    $newEventPushNotification: Boolean
  ) {
    updateUser(
      input: {
        profileImage: $profileImage
        firstName: $firstName
        lastName: $lastName
        birthDate: $birthDate
        phoneNumber: $phoneNumber
        allowNewEventEmails: $allowNewEventEmails
        marketingOptIn: $marketingOptIn
        newEventPushNotification: $newEventPushNotification
      }
    )
  }
`;

/**
 * Used to upload User Profile image
 *
 * @param userId (not used, taken from auth token)
 * @param file
 */
export const uploadUserProfileImage = async (
  _: string,
  file: File
): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await restClient.post(
      `/api/upload/users/profile-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error("Failed to upload file");
    }

    const { destinationUrl } = response.data;
    return destinationUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw new Error("Failed to upload file");
  }
};

export const CONFIRM_EMAIL = gql`
  mutation ConfirmEmail($confirmToken: String!) {
    confirmEmail(confirmToken: $confirmToken)
  }
`;

export const REQUEST_DELETE_ACCOUNT = gql`
  mutation RequestUserDeletion {
      requestUserDeletion
  }
`;

/**
 * Downloads a JSON export of the current user's own personal data (GDPR Art. 20 data
 * portability self-service export) and triggers a browser save-as. The filename is taken
 * from the server's Content-Disposition header.
 */
export const downloadUserDataExport = async (): Promise<void> => {
  const response = await restClient.get("/api/users/me/data-export", {
    responseType: "blob",
  });

  const contentDisposition = response.headers?.["content-disposition"] as
    | string
    | undefined;
  const match = contentDisposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? "giojoy-data-export.json";

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const GET_WALLET_BALANCE = gql`
  query GetWalletBalance {
    walletBalance {
      availableBalance
      totalBalance
    }
  }
`;

export const GET_MY_PAYMENT_DATA = gql`
  query MyPaymentData {
    myPaymentData {
      method
      bankIban
      bankAccountHolder
    }
  }
`;

export const UPDATE_MY_PAYMENT_DATA = gql`
  mutation UpdateMyPaymentData($input: UpdatePaymentDataInput!) {
    updateMyPaymentData(input: $input) {
      id
      method
      bankIban
      bankAccountHolder
    }
  }
`;

export const REQUEST_WITHDRAWAL = gql`
  mutation RequestWithdrawal($amountCents: Int!, $currency: String!, $method: String!, $destination: String) {
    requestWithdrawal(amountCents: $amountCents, currency: $currency, method: $method, destination: $destination) {
      id
      amountCents
      currency
      method
      status
    }
  }
`;

export const GET_MY_WITHDRAWAL_REQUESTS = gql`
  query MyWithdrawalRequests($limit: Int, $status: String) {
    myWithdrawalRequests(limit: $limit, status: $status) {
      id
      amountCents
      currency
      method
      status
      createdAt
    }
  }
`;

export const GET_INVITATION_RELATIONSHIPS = gql`
  query GetInvitationRelationships {
    myInvitationRelationships {
      invitedBy {
        userId
        firstName
        lastName
        profileImage
        registeredAt
      }
      invitedUsers {
        userId
        firstName
        lastName
        profileImage
        registeredAt
      }
    }
  }
`;

export const UPDATE_BILLING_ADDRESS = gql`
  mutation UpdateBillingAddress($input: BillingAddressInput!) {
    updateBillingAddress(input: $input)
  }
`;

export const MY_RELIABILITY_HISTORY_QUERY = gql`
  query MyReliabilityHistory($first: Int, $after: String) {
    myReliabilityHistory(first: $first, after: $after) {
      id
      delta
      reason
      eventId
      scoreBefore
      scoreAfter
      historical
      createdAt
    }
  }
`;

export const GET_MY_WHATSAPP_CONSENT = gql`
  query GetMyWhatsAppConsent {
    myWhatsAppConsent {
      __typename
      status
      phoneNumber
      optedInAt
      confirmedAt
      optedOutAt
    }
  }
`;

export const REQUEST_WHATSAPP_OPT_IN = gql`
  mutation RequestWhatsAppOptIn(
    $phoneNumber: String!
    $source: WhatsAppConsentSourceInput!
  ) {
    requestWhatsAppOptIn(input: { phoneNumber: $phoneNumber, source: $source }) {
      __typename
      status
      phoneNumber
      optedInAt
      confirmedAt
      optedOutAt
    }
  }
`;

export const OPT_OUT_WHATSAPP = gql`
  mutation OptOutWhatsApp {
    optOutWhatsApp {
      __typename
      status
      phoneNumber
      optedInAt
      confirmedAt
      optedOutAt
    }
  }
`;